import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';
import ThemeContext from '../utils/theming';

import {
    Content,
    Description,
    Details,
    DetailsContainer,
    Footer as DefaultFooter,
    Title
} from '../containers/DefaultStyles/Shared';
import { MultiImageBlock } from './Image/MultiImageBlock';
import { isLongText } from './Shared/commonFunctions';
import ShareDrawer from './ShareDrawer';

import { setOverlayPartnerVisible } from '../containers/Widget/actions';
import { Placeholders } from '../utils/fillDynamicText';
import {
    relevantRepresentatives,
    joinEnglish,
    districtInfo
} from '../utils/localRepresentative/parser';
import { DefaultRepImageURL } from '../containers/DefaultStyles/Shared';
import { copyFor } from '../copy/copy';
import NextAction from '../containers/Widget/NextAction';

const Footer = styled(DefaultFooter)`
    position: absolute;
    bottom: 0;
    left: 0;
`;

const Info = styled.div`
    margin-top: 10px;
`;
const TextRow = styled.div`
    margin-top: 10px;
    a {
        color: ${({ theme }) => theme.colors.primary};
        text-decoration: none;
    }
`;

const RepsNotContactable = props => {
    return (
        <div>
            <Content
                isContactAction={true}
                useLocalRepresentative={true}
                isSingleAction={true}
            >
                <Title
                    isShortText={!isLongText(props.cardTitle, 28)}
                    isLongText={isLongText(props.cardTitle, 60)}
                    isExtraLongText={isLongText(props.cardTitle, 95)}
                    onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                    dangerouslySetInnerHTML={{
                        __html: props.cardTitle
                    }}
                />
                <DetailsContainer>
                    <MultiImageBlock
                        size={props.scrollableDesign && 90}
                        isLongTitle={isLongText(props.cardTitle, 40)}
                        imgSources={props.localRepImages}
                    />
                    <Details hasPhoto={true}>
                        <Description
                            bold
                            dangerouslySetInnerHTML={{
                                __html: props.localRepNames
                            }}
                            isSingleAction={true}
                            isLongText={isLongText(props.localRepNames, 90)}
                            isExtraLongText={isLongText(props.localRepNames, 160)}
                        />
                    </Details>
                </DetailsContainer>
            </Content>
            <Info>
                <TextRow theme={useContext(ThemeContext)}>
                    {props.localRepImages.length > 1
                        ? copyFor('findReps', 'noContactPlural', props.activeAction.type)
                        : copyFor(
                              'findReps',
                              'noContactSingular',
                              props.activeAction.type,
                              undefined,
                              { title: props.title }
                          )}
                </TextRow>
                <TextRow
                    theme={useContext(ThemeContext)}
                    dangerouslySetInnerHTML={{
                        __html: props.websiteText
                    }}
                />
            </Info>
            <Footer>
                <ShareDrawer fullwidth={true} backgroundColor={'#fff'} />
            </Footer>
            <NextAction
                exitingAction={props.activeAction}
                beforeAdvancing={props.beforeAdvancing}
            />
        </div>
    );
};

RepsNotContactable.propTypes = {
    activeAction: PropTypes.object.isRequired,
    beforeAdvancing: PropTypes.func.isRequired,
    cardTitle: PropTypes.string.isRequired,
    localRepImages: PropTypes.array.isRequired,
    localRepNames: PropTypes.string.isRequired,
    setOverlayPartnerVisible: PropTypes.func,
    title: PropTypes.string,
    websiteText: PropTypes.string.isRequired,
    scrollableDesign: PropTypes.bool
};

export default connect(
    (state, props) => {
        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  props.activeAction.representativeSearch,
                  state.localRep.localRepData.representative
              )
            : [];
        const country = ((state.localRep.localRepData || {}).address || {}).country;

        const localRepNames = joinEnglish(
            reps.map(rep =>
                rep.title
                    ? `${rep.title} ${rep.fullName}${districtInfo(rep)}`
                    : `${rep.displayName}${districtInfo(rep)}`
            )
        );
        const currCard = state.widget.cards[props.activeAction.order - 1];

        const websites = reps
            .filter(rep => rep.website)
            .map(rep => ({ name: rep.fullName, website: rep.website }));
        let websiteText;
        if (websites.length === 1) {
            websiteText = copyFor(
                'findReps',
                'websiteInfo',
                props.activeAction.type,
                undefined,
                {
                    link: `<a href="${
                        websites[0].website
                    }" target="_blank" rel="noopener noreferrer">`,
                    endLink: '</a>'
                }
            );
        } else {
            websiteText = copyFor('findReps', 'websitesInfo', props.activeAction.type);
            websiteText += ' ';
            websiteText += websites
                .map(
                    w =>
                        `<a href="${
                            w.website
                        }" target="_blank" rel="noopener noreferrer">${w.name}</a>`
                )
                .join(', ');
        }

        return {
            cardTitle: (currCard.heading || props.activeAction.name || '')
                .replace(/@articleUrl/g, state.widget.articleUrl)
                .replace(
                    new RegExp(Placeholders.PostalCodePlaceholder, 'g'),
                    (state.localRep.localRepData.address || {}).zipCode
                )
                .replace(
                    new RegExp(
                        props.type === 'tweet'
                            ? Placeholders.TwitterMentionPlaceholder
                            : Placeholders.LocalRepresentative,
                        'g'
                    ),
                    localRepNames
                )
                .replace(
                    new RegExp(Placeholders.TwitterMentionPlaceholder, 'g'),
                    joinEnglish(reps.map(rep => rep.fullName || rep.twitterMention))
                ),
            localRepImages: reps.map(rep => rep.photoUrl || DefaultRepImageURL(country)),
            localRepNames,
            title: ((reps[0] || {}).title || 'representative').toLowerCase(),
            websiteText
        };
    },
    { setOverlayPartnerVisible }
)(RepsNotContactable);
